import { Head, router } from "@inertiajs/react"
import { Bell, CheckCheck } from "lucide-react"

import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { t } from "@/lib/i18n"
import { formatRelativeTime } from "@/lib/time"
import type { NotificationData } from "@/types"
import type { NotificationsIndex as NotificationsIndexProps } from "@/types/serializers"

export default function NotificationsIndex({
  notifications,
  pagination,
}: NotificationsIndexProps) {
  function handleClick(notification: NotificationData) {
    if (notification.read_at && notification.url) {
      router.visit(notification.url)
    } else if (!notification.read_at) {
      router.patch(`/notificaciones/${notification.id}`)
    }
  }

  function handleMarkAllRead() {
    router.post("/notificaciones/mark_all_read")
  }

  function goToPage(page: number) {
    router.visit(`/notificaciones?page=${page}`)
  }

  const hasUnread = notifications.some((n) => !n.read_at)

  return (
    <AppLayout>
      <Head title="Notificaciones | KrownFT" />
      <div className="bg-background min-h-svh">
        <div className="mx-auto max-w-2xl px-4 py-6">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="text-krown-100 h-5 w-5" />
              <h1 className="text-2xl font-bold">Notificaciones</h1>
            </div>
            {hasUnread && (
              <Button
                variant="ghost"
                size="sm"
                className="text-krown-100 hover:text-krown-50 text-xs"
                onClick={handleMarkAllRead}
              >
                <CheckCheck className="mr-1 h-4 w-4" />
                Marcar todas leídas
              </Button>
            )}
          </div>

          {notifications.length > 0 ? (
            <div className="divide-border border-border bg-card divide-y rounded-lg border">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  data-notification-id={n.id}
                  className={`hover:bg-accent flex cursor-pointer gap-3 px-4 py-3 transition-colors ${
                    !n.read_at
                      ? "border-krown-200 bg-krown-200/5 border-l-2"
                      : ""
                  }`}
                  onClick={() => handleClick(n)}
                >
                  <div className="bg-krown-200 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white">
                    {n.actor.first_name[0]}
                    {n.actor.last_name[0]}
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-sm ${!n.read_at ? "font-semibold" : ""}`}
                    >
                      <span>{n.actor.full_name}</span>{" "}
                      <span className="text-muted-foreground">
                        {t.notifications.actions[n.action] ?? n.action}
                      </span>
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {formatRelativeTime(n.created_at)}
                    </p>
                  </div>
                  {!n.read_at && (
                    <div className="flex items-center">
                      <span className="bg-krown-100 h-2 w-2 rounded-full" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Bell className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
              <p className="text-muted-foreground">
                {t.notifications.noNotifications}
              </p>
            </div>
          )}

          {pagination.pages > 1 && (
            <div className="mt-6 flex justify-center gap-2">
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
                (page) => (
                  <Button
                    key={page}
                    variant={page === pagination.page ? "default" : "outline"}
                    size="sm"
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </Button>
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
